import { Component } from '@theme/component';

/**
 * Screen time calculator.
 *
 * Draws a whole life as a grid of dots - one per month (or week) of an average
 * lifespan - and colours in how much of it goes to a screen. Nothing is
 * submitted anywhere; it exists to make a number feel like something.
 *
 * The grid is built once on connect and never rebuilt. Redrawing 4160 nodes on
 * every slider frame is what makes this kind of visual stutter, so instead each
 * dot's state is cached and only written when it actually changes. Dragging a
 * slider moves a boundary by a handful of dots, so a frame typically touches a
 * handful of nodes rather than thousands.
 *
 * The dots are decorative: the grid carries role="img" and a text label
 * describing the result, so assistive tech gets the finding rather than
 * thousands of anonymous children.
 *
 * @typedef {Object} Refs
 * @property {HTMLScriptElement} data
 * @property {HTMLElement} grid
 * @property {HTMLInputElement} [ageRange]
 * @property {HTMLInputElement} [ageNumber]
 * @property {HTMLInputElement} [hoursRange]
 * @property {HTMLInputElement} [hoursNumber]
 * @property {HTMLElement} [headlineValue]
 * @property {HTMLElement} [statScreenDays]
 * @property {HTMLElement} [statWakingPercent]
 * @property {HTMLElement} [statDaysPerYear]
 * @property {HTMLElement} [statYearsAhead]
 * @property {HTMLElement[]} [legendCounts]
 * @extends {Component<Refs>}
 */
export class ScreenTimeCalculatorComponent extends Component {
  requiredRefs = ['data', 'grid'];

  /** @type {{ lifespan: number, unitsPerYear: number, sleepHours: number, strings: Record<string,string> }} */
  #config = { lifespan: 80, unitsPerYear: 12, sleepHours: 8, strings: {} };

  /** @type {HTMLElement[]} */
  #dots = [];

  /** Last state written to each dot, so only real changes hit the DOM. */
  #states = [];

  #age = 30;
  #hours = 4;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#config = { ...this.#config, ...JSON.parse(this.refs.data.textContent ?? '{}') };
    } catch {
      // Leave the server-rendered numbers in place rather than blanking them.
      return;
    }

    this.#age = Number(this.refs.ageRange?.value ?? this.#age);
    this.#hours = Number(this.refs.hoursRange?.value ?? this.#hours);

    this.#buildGrid();
    this.#render();
  }

  #totalUnits() {
    return Math.round(this.#config.lifespan * this.#config.unitsPerYear);
  }

  #buildGrid() {
    const total = this.#totalUnits();
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < total; i++) {
      const dot = document.createElement('span');
      dot.className = 'stc__dot';
      dot.dataset.state = 'future-life';
      fragment.appendChild(dot);
      this.#dots.push(dot);
      this.#states.push('future-life');
    }

    this.refs.grid.appendChild(fragment);
  }

  /**
   * Segment sizes, in dots, in life order: screen time already spent, the rest
   * of the life lived, screen time still to come, then whatever is left.
   */
  #segments() {
    const total = this.#totalUnits();
    const { lifespan, unitsPerYear } = this.#config;

    const lived = Math.min(total, Math.max(0, Math.round(Math.min(this.#age, lifespan) * unitsPerYear)));
    const remaining = total - lived;
    const fraction = Math.min(1, Math.max(0, this.#hours / 24));

    const pastScreen = Math.round(lived * fraction);
    const futureScreen = Math.round(remaining * fraction);

    return {
      'past-screen': pastScreen,
      'past-life': lived - pastScreen,
      'future-screen': futureScreen,
      'future-life': remaining - futureScreen,
    };
  }

  #paintGrid() {
    const segments = this.#segments();
    let index = 0;

    for (const [state, count] of Object.entries(segments)) {
      for (let i = 0; i < count; i++, index++) {
        const dot = this.#dots[index];
        if (!dot) break;
        // The diff is the whole point - see the note at the top.
        if (this.#states[index] === state) continue;
        this.#states[index] = state;
        dot.dataset.state = state;
      }
    }
  }

  /**
   * @param {number} value
   * @param {number} decimals
   */
  #num(value, decimals = 0) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  /**
   * @param {string} template
   * @param {Record<string, string>} values
   */
  #fill(template, values) {
    let out = template ?? '';
    for (const [key, value] of Object.entries(values)) {
      out = out.replaceAll(`[${key}]`, value);
    }
    return out;
  }

  #render() {
    this.#paintGrid();

    const { lifespan, sleepHours, strings } = this.#config;
    const age = Math.min(this.#age, lifespan);
    const hours = this.#hours;

    const screenYearsTotal = (lifespan * hours) / 24;
    const screenYearsAhead = ((lifespan - age) * hours) / 24;
    const screenDaysTotal = screenYearsTotal * 365.25;
    const daysPerYear = (365.25 * hours) / 24;
    // Against waking hours, not the full 24 - sleeping isn't time you could
    // have spent otherwise, so the share of waking life is the honest figure.
    const wakingHours = Math.max(1, 24 - sleepHours);
    const wakingPercent = Math.min(100, (hours / wakingHours) * 100);

    const values = {
      years: this.#num(screenYearsTotal, 1),
      yearsAhead: this.#num(screenYearsAhead, 1),
      days: this.#num(screenDaysTotal),
      daysPerYear: this.#num(daysPerYear),
      percent: this.#num(wakingPercent),
      hours: this.#num(hours, hours % 1 === 0 ? 0 : 1),
      age: this.#num(age),
      lifespan: this.#num(lifespan),
    };

    const set = (/** @type {HTMLElement | undefined} */ el, /** @type {string} */ key) => {
      if (el) el.textContent = this.#fill(strings[key], values);
    };

    set(this.refs.headlineValue, 'headlineValue');
    set(this.refs.statScreenDays, 'statScreenDays');
    set(this.refs.statWakingPercent, 'statWakingPercent');
    set(this.refs.statDaysPerYear, 'statDaysPerYear');
    set(this.refs.statYearsAhead, 'statYearsAhead');

    this.refs.grid.setAttribute('aria-label', this.#fill(strings.gridLabel, values));
  }

  /** @param {number} value @param {number} min @param {number} max */
  #clamp(value, min, max) {
    if (!Number.isFinite(value)) return min;
    return Math.min(max, Math.max(min, value));
  }

  /**
   * Both inputs drive one value, so neither can drift from the other. As in the
   * wholesale calculator, the number box is only corrected on change/blur -
   * clamping per keystroke makes a two-digit entry impossible to type.
   *
   * @param {'age' | 'hours'} field
   * @param {number} value
   * @param {'range' | 'number' | null} source
   */
  #set(field, value, source) {
    const range = field === 'age' ? this.refs.ageRange : this.refs.hoursRange;
    const number = field === 'age' ? this.refs.ageNumber : this.refs.hoursNumber;
    const min = Number(range?.min ?? 0);
    const max = Number(range?.max ?? 100);
    const step = Number(range?.step ?? 1);

    let next = this.#clamp(value, min, max);
    next = Math.round(next / step) * step;
    // Rounding by a fractional step reintroduces float noise (0.5 steps give
    // 4.300000000000001), so snap back to the step's own precision.
    const decimals = (String(step).split('.')[1] ?? '').length;
    next = Number(next.toFixed(decimals));

    if (field === 'age') this.#age = next;
    else this.#hours = next;

    if (range && source !== 'range') range.value = String(next);
    if (number && source !== 'number') number.value = String(next);

    this.#render();
  }

  /** Fires while the age slider moves. */
  ageInput() {
    this.#set('age', Number(this.refs.ageRange?.value), 'range');
  }

  /** Fires on each keystroke in the age box; ignores values not yet in range. */
  ageNumberInput() {
    const raw = this.refs.ageNumber?.value;
    if (raw === '' || raw == null) return;
    const value = Number(raw);
    if (!Number.isFinite(value)) return;
    if (value < Number(this.refs.ageRange?.min ?? 0) || value > Number(this.refs.ageRange?.max ?? 100)) return;
    this.#set('age', value, 'number');
  }

  /** Fires on blur / enter, where correcting an out-of-range value is welcome. */
  ageNumberCommit() {
    this.#set('age', Number(this.refs.ageNumber?.value), null);
  }

  /** Fires while the hours slider moves. */
  hoursInput() {
    this.#set('hours', Number(this.refs.hoursRange?.value), 'range');
  }

  /** Fires on each keystroke in the hours box. */
  hoursNumberInput() {
    const raw = this.refs.hoursNumber?.value;
    if (raw === '' || raw == null) return;
    const value = Number(raw);
    if (!Number.isFinite(value)) return;
    if (value < Number(this.refs.hoursRange?.min ?? 0) || value > Number(this.refs.hoursRange?.max ?? 24)) return;
    this.#set('hours', value, 'number');
  }

  /** Fires on blur / enter for the hours box. */
  hoursNumberCommit() {
    this.#set('hours', Number(this.refs.hoursNumber?.value), null);
  }
}

if (!customElements.get('screen-time-calculator-component')) {
  customElements.define('screen-time-calculator-component', ScreenTimeCalculatorComponent);
}
